import React, { useState, useEffect } from 'react';
import { getOrderPayableTotal } from '../../utils/orderAmount';
import { getCategoryChip } from '../../utils/customerCategory';
import { useNavigate } from 'react-router-dom';
import {
  Paper,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  Button,
  Chip,
  IconButton,
  Card,
  CardContent,
  Grid,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  Receipt as ReceiptIcon,
  TrendingUp as TrendingUpIcon,
  ShoppingCart as ShoppingCartIcon,
  AttachMoney as AttachMoneyIcon,
  HourglassEmpty as PendingIcon,
  CheckCircle as ConfirmedIcon,
  Assignment as AssignedOrderIcon,
  LocalShipping as OutForDeliveryIcon,
  CheckCircleOutline as DeliveredIcon,
  Cancel as CancelledIcon,
  AssignmentReturn as ReturnedIcon,
  FileDownload as ExportIcon,
  Print as PrintIcon,
  Map as MapIcon
} from '@mui/icons-material';
import { useApp } from '../../context/AppContext';
import api from '../../utils/api';
import OrderModal from '../../components/common/OrderModal';
import PartialDeliveryModal from '../../components/common/PartialDeliveryModal';
import StatusReasonDialog from '../../components/common/StatusReasonDialog';
import { cacheEndpoints } from '../../utils/apiCache';
import RiderAllocationModal from '../../components/common/RiderAllocationModal';
import OrderMapSelector from '../../components/common/OrderMapSelector';
import { fetchAndOpenKotPdfs } from '../../utils/kotPdf';


const OrderList = () => {
  const navigate = useNavigate();
  const { actions, state } = useApp();
  
  // Check if user is super admin
  const isSuperAdmin = state.user?.role === 'super_admin' || state.user?.role === 'super-admin';

  // State management
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [branchFilter, setBranchFilter] = useState('all');
  const [timeSlotFilter, setTimeSlotFilter] = useState('all');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [stores, setStores] = useState([]);
  const [timeSlots, setTimeSlots] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalOrders, setTotalOrders] = useState(0);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    confirmed: 0,
    processing: 0,
    assigned: 0,
    out_for_delivery: 0,
    delivered: 0,
    completed: 0,
    cancelled: 0,
    returned: 0
  });
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderModalOpen, setOrderModalOpen] = useState(false);
  const [partialDeliveryOrder, setPartialDeliveryOrder] = useState(null);
  const [partialDeliveryOpen, setPartialDeliveryOpen] = useState(false);

  // State for status updates
  const [updatingStatus, setUpdatingStatus] = useState({});
  
  // State for status reason dialog
  const [statusReasonDialog, setStatusReasonDialog] = useState({
    open: false,
    orderId: null,
    newStatus: null,
    prevStatus: null,
    loading: false
  });

  // State for export functionality
  const [exporting, setExporting] = useState(false);

  // State for order selection and allocation
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [allocationModalOpen, setAllocationModalOpen] = useState(false);
  const [routePlannerOpen, setRoutePlannerOpen] = useState(false);
  const [printingKot, setPrintingKot] = useState(false);
  
  // State for duplicate order error modal
  const [duplicateOrderError, setDuplicateOrderError] = useState({
    open: false,
    duplicateOrders: [] // Array of {orderId, routeId}
  });

  useEffect(() => {
    fetchStores();
    fetchTimeSlots();
    fetchStats();
  }, []);

  useEffect(() => {
    // Apply current filters when page changes (but only for pagination, not automatic filtering)
    const searchParams = {
      search: searchTerm,
      from_date: fromDate,
      to_date: toDate
    };
    fetchOrders(searchParams);
  }, [page, rowsPerPage]);

  const fetchStores = async () => {
    try {
      const storesData = await cacheEndpoints.stores.getAll();
      setStores(storesData);
    } catch (error) {
      console.error('Error fetching stores:', error);
    }
  };

  const fetchTimeSlots = async () => {
    try {
      const response = await api.get('/time-slots');
      if (response.data.success) {
        setTimeSlots(response.data.timeSlots || []);
      }
    } catch (error) {
      console.error('Error fetching time slots:', error);
    }
  };

  const fetchOrders = async (searchParams = {}) => {
    try {
      setLoading(true);
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        search: searchParams.search !== undefined ? searchParams.search : searchTerm,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        branch_id: branchFilter !== 'all' ? branchFilter : undefined,
        timeSlotId: timeSlotFilter !== 'all' ? timeSlotFilter : undefined,
        from_date: searchParams.from_date !== undefined ? searchParams.from_date : fromDate,
        to_date: searchParams.to_date !== undefined ? searchParams.to_date : toDate
      };

      const response = await api.get('/orders', { params });
      if (response.data.success) {
        // Orders are already in PILOT structure from server
        setOrders(response.data.orders || []);
        setTotalOrders(response.data.total || 0);
        setError(''); // Clear any previous errors on success
      } else {
        setError('Failed to fetch orders');
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
      setError(error.response?.data?.message || 'Error fetching orders');
      actions.showSnackbar('Error fetching orders', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async (searchParams = {}) => {
    try {
      const params = {
        status: statusFilter !== 'all' ? statusFilter : undefined,
        branch_id: branchFilter !== 'all' ? branchFilter : undefined,
        timeSlotId: timeSlotFilter !== 'all' ? timeSlotFilter : undefined,
        from_date: searchParams.from_date !== undefined ? searchParams.from_date : fromDate,
        to_date: searchParams.to_date !== undefined ? searchParams.to_date : toDate
      };

      const response = await api.get('/orders/stats', { params });
      if (response.data.success) {
        setStats(response.data.stats);
      }
    } catch (error) {
      console.error('Error fetching order stats:', error);
    }
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleSearch = () => {
    setPage(0);
    setError(''); // Clear error when initiating search
    const searchParams = {
      search: searchTerm,
      from_date: fromDate,
      to_date: toDate
    };
    fetchOrders(searchParams);
    fetchStats(searchParams);
  };

  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
    setPage(0); // Reset to first page when filtering
    setError(''); // Clear error when changing filters
  };

  const handleBranchFilterChange = (event) => {
    setBranchFilter(event.target.value);
    setPage(0); // Reset to first page when filtering
    setError(''); // Clear error when changing filters
  };

  const handleTimeSlotFilterChange = (event) => {
    setTimeSlotFilter(event.target.value);
    setPage(0); // Reset to first page when filtering
    setError(''); // Clear error when changing filters
  };

  const handleFromDateChange = (event) => {
    setFromDate(event.target.value);
  };

  const handleToDateChange = (event) => {
    setToDate(event.target.value);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    // Scroll to top when page changes
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewOrder = async (orderId) => {
    try {
      const response = await api.get(`/orders/${orderId}`);
      if (response.data.success) {
        // Order is already in PILOT structure from server
        setSelectedOrder(response.data.order);
        setOrderModalOpen(true);
      } else {
        actions.showSnackbar('Order not found', 'error');
      }
    } catch (error) {
      console.error('Error fetching order details:', error);
      actions.showSnackbar('Error fetching order details', 'error');
    }
  };

  const handleCloseOrderModal = () => {
    setOrderModalOpen(false);
    setSelectedOrder(null);
  };

  const handleOrderUpdated = () => {
    // Refresh orders list and stats when an order is updated
    fetchOrders();
    fetchStats();
  };

  // Order selection handlers
  const handleSelectOrder = (orderId) => {
    setSelectedOrders(prev => {
      if (prev.includes(orderId)) {
        return prev.filter(id => id !== orderId);
      } else {
        return [...prev, orderId];
      }
    });
  };

  const handleSelectAllOrders = (event) => {
    if (event.target.checked) {
      const allOrderIds = orders
        .filter((o) => !isInStoreOrder(o))
        .map((order) => getOrderId(order));
      setSelectedOrders(allOrderIds);
    } else {
      setSelectedOrders([]);
    }
  };

  const handleOpenAllocationModal = () => {
    // Check if any selected orders already have a routeId
    const duplicates = [];
    
    selectedOrders.forEach(orderId => {
      const order = orders.find(o => getOrderId(o) === orderId);
      if (order && order.routeId) {
        duplicates.push({
          orderId: order.orderId || orderId,
          routeId: order.routeId
        });
      }
    });
    
    if (duplicates.length > 0) {
      // Show error modal if any duplicates found
      setDuplicateOrderError({
        open: true,
        duplicateOrders: duplicates
      });
    } else {
      // No duplicates, proceed to allocation modal
      setAllocationModalOpen(true);
    }
  };

  const handlePrintKot = async () => {
    if (selectedOrders.length === 0) {
      actions.showSnackbar('Please select at least one order', 'warning');
      return;
    }

    try {
      setPrintingKot(true);
      const { orderCount, opened } = await fetchAndOpenKotPdfs(selectedOrders);
      actions.showSnackbar(
        opened
          ? `Opened KOT PDF for ${orderCount} order${orderCount === 1 ? '' : 's'}`
          : `Downloaded KOT PDF for ${orderCount} order${orderCount === 1 ? '' : 's'} (popup blocked)`,
        'success'
      );
    } catch (err) {
      console.error('Error printing KOT:', err);
      actions.showSnackbar(err.message || 'Failed to generate KOT PDF', 'error');
    } finally {
      setPrintingKot(false);
    }
  };

  const handleCloseAllocationModal = () => {
    setAllocationModalOpen(false);
  };
  
  const handleCloseDuplicateError = () => {
    setDuplicateOrderError({
      open: false,
      duplicateOrders: []
    });
  };

  const handleOrderAllocation = (rider, orderIds) => {
    // Clear selected orders after successful allocation
    setSelectedOrders([]);
    // Refresh orders list to show updated allocation status
    const searchParams = {
      search: searchTerm,
      from_date: fromDate,
      to_date: toDate
    };
    fetchOrders(searchParams);
    fetchStats(searchParams);
  };

  const handleRefresh = () => {
    const searchParams = {
      search: searchTerm,
      from_date: fromDate,
      to_date: toDate
    };
    fetchOrders(searchParams);
    fetchStats(searchParams);
  };

  // Status update function
  const handleStatusChange = (orderId, newStatus) => {
    if (!newStatus) return;

    const currentOrder = orders.find(order => getOrderId(order) === orderId);
    const prevStatus = currentOrder ? getOrderStatus(currentOrder) : null;

    // Restrict "delivered" status to only be allowed from "out_for_delivery"
    if (newStatus === 'delivered' && prevStatus !== 'out_for_delivery') {
      actions.showSnackbar('Order can only be marked as Delivered if current status is Out for Delivery', 'error');
      return;
    }

    // Check if status requires reason
    if (newStatus === 'cancelled' || newStatus === 'returned') {
      setStatusReasonDialog({
        open: true,
        orderId,
        newStatus,
        prevStatus,
        loading: false
      });
    } else {
      // Direct status update for other statuses
      handleStatusSave(orderId, newStatus, null, prevStatus);
    }
  };

  const handleStatusSave = async (orderId, newStatus, statusReason = null, prevStatus = null) => {
    try {
      setUpdatingStatus(prev => ({ ...prev, [orderId]: true }));
      
      const orderData = { status: newStatus };
      if (statusReason) {
        orderData.statusReason = statusReason;
      }
      if (prevStatus) {
        orderData.prevOrderStatus = prevStatus;
      }
      
      const response = await api.put(`/orders/${orderId}`, { orderData });
      
      if (response.data.success) {
        actions.showSnackbar('Order status updated successfully', 'success');
        
        // Update the order in the local state
        setOrders(prevOrders => 
          prevOrders.map(order => 
            getOrderId(order) === orderId 
              ? { ...order, order_status: newStatus, status: newStatus, statusReason, prevOrderStatus: prevStatus }
              : order
          )
        );
        
        // Refresh stats to reflect the change
        const searchParams = {
          search: searchTerm,
          from_date: fromDate,
          to_date: toDate
        };
        fetchStats(searchParams);
      } else {
        actions.showSnackbar('Failed to update order status', 'error');
      }
    } catch (error) {
      console.error('Error updating order status:', error);
      actions.showSnackbar('Error updating order status', 'error');
    } finally {
      setUpdatingStatus(prev => {
        const newUpdating = { ...prev };
        delete newUpdating[orderId];
        return newUpdating;
      });
    }
  };

  const handleStatusReasonConfirm = async (reason) => {
    setStatusReasonDialog(prev => ({ ...prev, loading: true }));
    
    try {
      await handleStatusSave(statusReasonDialog.orderId, statusReasonDialog.newStatus, reason, statusReasonDialog.prevStatus);
      setStatusReasonDialog({ open: false, orderId: null, newStatus: null, prevStatus: null, loading: false });
    } catch (error) {
      setStatusReasonDialog(prev => ({ ...prev, loading: false }));
    }
  };

  const handleStatusReasonCancel = () => {
    setStatusReasonDialog({ open: false, orderId: null, newStatus: null, prevStatus: null, loading: false });
  };

  // Export functionality
  const validateDateRange = () => {
    if (!fromDate || !toDate) {
      return "Please select both start and end dates for export";
    }

    const startDate = new Date(fromDate);
    const endDate = new Date(toDate);
    const timeDiff = endDate.getTime() - startDate.getTime();
    const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));

    if (daysDiff > 31) {
      return "Date range cannot exceed one month (31 days) for export";
    }

    if (daysDiff < 0) {
      return "End date must be after start date";
    }

    return null;
  };

  const handleExport = async () => {
    const validationError = validateDateRange();
    if (validationError) {
      actions.showSnackbar(validationError, 'error');
      return;
    }

    try {
      setExporting(true);
      setError('');

      // Build query parameters
      const params = {
        startDate: fromDate,
        endDate: toDate,
        format: 'excel'
      };

      // Add store filter
      if (branchFilter !== 'all') {
        params.storeId = branchFilter;
      }

      // Add status filter
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }

      // Add timeSlot filter
      if (timeSlotFilter !== 'all') {
        params.timeSlotId = timeSlotFilter;
      }

      console.log('Generating sales report with params:', params);

      // Make API call to download Excel
      const response = await api.get('/reports/sales/export', {
        params,
        responseType: 'blob' // Important for file download
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;

      // Generate filename with current timestamp
      const timestamp = new Date().toISOString().split('T')[0];
      const statusSuffix = statusFilter !== 'all' ? `_${statusFilter}` : '';
      const storeSuffix = branchFilter !== 'all' ? `_store_${branchFilter}` : '';
      const timeSlotSuffix = timeSlotFilter !== 'all' ? `_slot_${timeSlotFilter}` : '';

      link.setAttribute('download', `sales_report_${timestamp}${statusSuffix}${storeSuffix}${timeSlotSuffix}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      actions.showSnackbar('Sales report downloaded successfully!', 'success');

    } catch (error) {
      console.error('Error generating sales report:', error);
      actions.showSnackbar('Error generating sales report', 'error');
    } finally {
      setExporting(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    let date;
    if (typeof timestamp === 'number') {
      date = new Date(timestamp);
    } else if (typeof timestamp === 'string') {
      date = new Date(timestamp);
    } else if (timestamp?.toDate) {
      date = timestamp.toDate();
    } else {
      return 'N/A';
    }
    
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    if (!amount) return '₹0';
    return `₹${parseFloat(amount).toFixed(2)}`;
  };

  const getOrderStatus = (order) => {
    return order.order_status || 'pending';
  };

  const getOrderAmount = (order) => getOrderPayableTotal(order);

  const getOrderDate = (order) => {
    return order.timeStamp || order.createdAt;
  };

  const getOrderId = (order) => {
    return  order.orderId || order.id || 'N/A';
  };

  const renderOrderIdCell = (order) => (
    <TableCell>
      <Typography variant="body2" fontWeight="bold">
        #{order.orderId || 'N/A'}
      </Typography>
      {order.isSplitParent === true && (
        <Chip label="Partial Delivery" size="small" color="secondary" sx={{ mt: 0.5 }} />
      )}
      {order.parentOrderId && (
        <Chip
          label={`Child of #${order.parentOrderId}`}
          size="small"
          variant="outlined"
          onClick={(e) => {
            e.stopPropagation();
            handleViewOrder(order.parentOrderId);
          }}
          sx={{ mt: 0.5, cursor: 'pointer' }}
        />
      )}
    </TableCell>
  );

  const normalizeOrderType = (value) => {
    if (!value) return '';
    return String(value).trim().toLowerCase().replace(/[\s_-]+/g, ' ');
  };

  const isInStoreOrder = (order) => {
    // Primary signal in this codebase is usually delivery type (Home Delivery vs In Store)
    const raw =
      order?.deliveryType ??
      order?.delivery_type ??
      order?.delivery_type_name ??
      order?.deliveryTypeName ??
      // fallback: some payloads may use order type naming
      order?.orderType ??
      order?.order_type ??
      order?.orderTypeName ??
      order?.order_type_name ??
      order?.type;
    return normalizeOrderType(raw) === 'in store';
  };

  const getCustomerName = (order) => {
    return order.deliveryAddress?.userName || 'N/A';
  };

  const getCustomerMobile = (order) => {
    return order.userId || 'N/A';
  };

  const getCustomerInfo = (order) => {
    return getCustomerName(order);
  };

  const renderCustomerCell = (order) => {
    const chip = getCategoryChip(order.category);
    return (
      <Box>
        <Typography variant="body2">{getCustomerInfo(order)}</Typography>
        {chip && (
          <Chip
            label={chip.label}
            color={chip.color}
            size="small"
            variant="outlined"
            sx={{ mt: 0.5, height: 20, fontSize: '0.65rem' }}
          />
        )}
      </Box>
    );
  };

  const getOrderStatusColor = (status) => {
    const statusLower = status.toLowerCase();
    switch (statusLower) {
      case 'delivered':
      case 'completed':
        return 'success';
      case 'out_for_delivery':
      case 'processing':
      case 'confirmed':
        return 'warning';
      case 'assigned':
        return 'success';
      case 'returned':
        return 'warning'; // Orange color for returned orders
      case 'cancelled':
      case 'failed':
        return 'error';
      case 'pending':
        return 'info';
      default:
        return 'default';
    }
  };

  const getPaymentMethod = (order) => {
    const paymentMethod = order.payment_method || order.paymentMethod || '';
    
    // Transform payment method display
    const method = paymentMethod.toLowerCase();
    
    // Cash on Delivery variations
    if (method.includes('cash_on_delivery') || method.includes('cod') || method === 'cash') {
      return 'COD';
    }
    
    // Online payment variations
    if (method.includes('pay_by_wallet') || method.includes('online') || method.includes('card') || method.includes('upi')) {
      return 'Online';
    }
    
    // Default fallback
    return paymentMethod?.replace(/_/g, ' ') || 'N/A';
  };

  const getStoreName = (order) => {
    return order.storeDetails?.storeName || 'N/A';
  };

  const getDeliveryDate = (order) => {
    const deliveryDate = order.deliveryDate;
    if (!deliveryDate) return 'N/A';
    
    // Format delivery date without time
    let date;
    if (typeof deliveryDate === 'number') {
      date = new Date(deliveryDate);
    } else if (typeof deliveryDate === 'string') {
      date = new Date(deliveryDate);
    } else if (deliveryDate?.toDate) {
      date = deliveryDate.toDate();
    } else {
      return 'N/A';
    }
    
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getTimeSlotDisplay = (order) => {
    if (order.timeSlotDetails?.displayTime) {
      return order.timeSlotDetails.displayTime;
    }
    
    if (order.timeSlotDetails?.startTime && order.timeSlotDetails?.endTime) {
      return `${order.timeSlotDetails.startTime} - ${order.timeSlotDetails.endTime}`;
    }
    
    return 'N/A';
  };

  const statusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'pending', label: 'Pending' },
    { value: 'confirmed', label: 'Confirmed' },
    { value: 'processing', label: 'Processing' },
    { value: 'assigned', label: 'Assigned' },
    { value: 'out_for_delivery', label: 'Out for Delivery' },
    { value: 'delivered', label: 'Delivered' },
    { value: 'returned', label: 'Returned' },
    { value: 'cancelled', label: 'Cancelled' },
    { value: 'partial_delivery', label: 'Partial Delivery' }
  ];

  const statsCards = [
    {
      title: 'Pending',
      value: stats.pending || 0,
      icon: <PendingIcon sx={{ fontSize: 40 }} />,
      color: 'info.main'
    },
    {
      title: 'Confirmed',
      value: stats.confirmed || 0,
      icon: <ConfirmedIcon sx={{ fontSize: 40 }} />,
      color: 'warning.main'
    },
    {
      title: 'Assigned',
      value: stats.assigned || 0,
      icon: <AssignedOrderIcon sx={{ fontSize: 40 }} />,
      color: '#2e7d32'
    },
    {
      title: 'Out for Delivery',
      value: stats.out_for_delivery || 0,
      icon: <OutForDeliveryIcon sx={{ fontSize: 40 }} />,
      color: 'secondary.main'
    },
    {
      title: 'Delivered',
      value: stats.delivered || 0,
      icon: <DeliveredIcon sx={{ fontSize: 40 }} />,
      color: 'success.main'
    },
    {
      title: 'Cancelled',
      value: stats.cancelled || 0,
      icon: <CancelledIcon sx={{ fontSize: 40 }} />,
      color: 'error.main'
    },
    {
      title: 'Returned',
      value: stats.returned || 0,
      icon: <ReturnedIcon sx={{ fontSize: 40 }} />,
      color: 'warning.main'
    }
  ];

  if (loading && orders.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  const inStoreOrders = orders.filter(isInStoreOrder);
  const otherOrders = orders.filter((o) => !isInStoreOrder(o));

  const inStoreGlowSx = {
    '@keyframes inStoreGlow': {
      '0%': {
        boxShadow: '0 0 0 rgba(255, 152, 0, 0.0)',
        filter: 'brightness(1)'
      },
      '50%': {
        boxShadow: '0 0 14px rgba(255, 152, 0, 0.55)',
        filter: 'brightness(1.06)'
      },
      '100%': {
        boxShadow: '0 0 0 rgba(255, 152, 0, 0.0)',
        filter: 'brightness(1)'
      }
    },
    animation: 'inStoreGlow 1.4s ease-in-out infinite'
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" sx={{ mb: 1, fontWeight: 'bold' }}>
            Orders Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage and view all orders in the system
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<MapIcon />}
            onClick={() => setRoutePlannerOpen(true)}
          >
            Plan Route on Map
          </Button>
          <Button
            variant="outlined"
            onClick={handlePrintKot}
            disabled={selectedOrders.length === 0 || printingKot}
            startIcon={printingKot ? <CircularProgress size={16} /> : <PrintIcon />}
          >
            {printingKot ? 'Printing...' : `Print KOT (${selectedOrders.length})`}
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }} >
        {statsCards.map((stat, index) => (
          <Grid item key={index} sx={{ width: '30%' }}>
            <Card >
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 }, display: 'flex',  flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', textAlign: 'center' }}>
                  <Box sx={{ color: stat.color, display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                    {React.cloneElement(stat.icon, { sx: { fontSize: 25, mr: 1 } })}
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: 16, fontWeight: 'bold' }}>
                      {stat.title}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', ml: 2 }}>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                      {stat.value}
                    </Typography>
                   
                  </Box>
                  
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Controls */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* First Row: Filters (50%) and Search (50%) */}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            {/* Left Side: Store and Status Filters (50%) */}
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', width: '50%' }}>
              <FormControl sx={{ flex: 1 }}>
                <InputLabel>Store</InputLabel>
                <Select
                  value={branchFilter}
                  onChange={handleBranchFilterChange}
                  label="Store"
                >
                  <MenuItem value="all">All Stores</MenuItem>
                  {stores.map((store) => (
                    <MenuItem key={store.branchId} value={store.branchId}>
                      {store.storeName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl sx={{ flex: 1 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={handleStatusFilterChange}
                  label="Status"
                >
                  {statusOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Right Side: Search (50%) */}
            <Box sx={{ width: '50%' }}>
              <TextField
                placeholder="Search orders..."
                value={searchTerm}
                onChange={handleSearchChange}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleSearch();
                  }
                }}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </Box>

          {/* Second Row: Date Filters and Time Slot (50%) and Search Button (50%) */}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            {/* Left Side: Date Filters and Time Slot (50%) */}
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', width: '50%' }}>
              <TextField
                label="From Date"
                type="date"
                value={fromDate}
                onChange={handleFromDateChange}
                InputLabelProps={{
                  shrink: true,
                }}
                sx={{ flex: 1 }}
              />
              <TextField
                label="To Date"
                type="date"
                value={toDate}
                onChange={handleToDateChange}
                InputLabelProps={{
                  shrink: true,
                }}
                sx={{ flex: 1 }}
              />
              <FormControl sx={{ flex: 1 }}>
                <InputLabel>Time Slot</InputLabel>
                <Select
                  value={timeSlotFilter}
                  onChange={handleTimeSlotFilterChange}
                  label="Time Slot"
                >
                  <MenuItem value="all">All Time Slots</MenuItem>
                  {timeSlots.map((slot) => (
                    <MenuItem key={slot.id} value={slot.id}>
                      {slot.startTime && slot.endTime ? `${slot.startTime} - ${slot.endTime}` : slot.id}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Right Side: Search Button and Export Button (50%) */}
            <Box sx={{ width: '50%', display: 'flex', alignItems: 'center', gap: 2 }}>
              <Button
                variant="contained"
                onClick={handleSearch}
                sx={{ 
                  minWidth: 150,
                  px: 3,
                  height: '40px'
                }}
              >
                Search
              </Button>
              <Button
                variant="outlined"
                onClick={handleExport}
                disabled={exporting}
                startIcon={exporting ? <CircularProgress size={16} /> : <ExportIcon />}
                sx={{
                  minWidth: 120,
                  px: 2,
                  height: '40px',
                  mr: 1
                }}
              >
                {exporting ? 'Exporting...' : 'Export'}
              </Button>
              <Button
                variant="contained"
                onClick={handleOpenAllocationModal}
                disabled={selectedOrders.length === 0}
                sx={{ 
                  minWidth: 150,
                  px: 2,
                  height: '40px'
                }}
              >
                Create Route ({selectedOrders.length})
              </Button>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Orders Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <input
                    type="checkbox"
                    checked={
                      orders.filter((o) => !isInStoreOrder(o)).length > 0 &&
                      orders
                        .filter((o) => !isInStoreOrder(o))
                        .every((o) => selectedOrders.includes(getOrderId(o)))
                    }
                    onChange={handleSelectAllOrders}
                    style={{ cursor: 'pointer' }}
                  />
                </TableCell>
                <TableCell>Order ID</TableCell>
                <TableCell>Order Date</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>Store Name</TableCell>
                <TableCell>Delivery Date</TableCell>
                <TableCell>Time Slot</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Payment Method</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : orders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <Typography variant="body1" color="text.secondary">
                      No orders found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                <>
                  {inStoreOrders.length > 0 && (
                    <TableRow>
                      <TableCell colSpan={11} sx={{ backgroundColor: 'rgba(255, 152, 0, 0.08)' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip
                            label={`In-Store Customer Orders • ${inStoreOrders.length}`}
                            color="warning"
                            size="small"
                            sx={inStoreGlowSx}
                          />
                   
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}

                  {inStoreOrders.map((order) => (
                    <TableRow
                      key={getOrderId(order)}
                      hover
                      sx={{
                        backgroundColor: 'rgba(255, 152, 0, 0.12)',
                        ...inStoreGlowSx
                      }}
                    >
                    <TableCell padding="checkbox">
                      {/* In-store orders are prioritized and not selectable */}
                    </TableCell>
                    <TableCell>
                      {renderOrderIdCell(order)}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(getOrderDate(order))}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {renderCustomerCell(order)}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {getStoreName(order)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {getDeliveryDate(order)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {getTimeSlotDisplay(order)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {formatCurrency(getOrderAmount(order))}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <FormControl size="small" sx={{ minWidth: 140 }}>
                        <Select
                          value={getOrderStatus(order)}
                          onChange={(e) => handleStatusChange(getOrderId(order), e.target.value)}
                          disabled={updatingStatus[getOrderId(order)]}
                          sx={{
                            '& .MuiSelect-select': {
                              py: 1,
                              fontSize: '0.875rem'
                            }
                          }}
                        >
                          <MenuItem value="pending">
                            <Chip label="Pending" color="info" size="small" sx={{ minWidth: 70 }} />
                          </MenuItem>
                          <MenuItem value="confirmed">
                            <Chip label="Confirmed" color="warning" size="small" sx={{ minWidth: 70 }} />
                          </MenuItem>
                          <MenuItem value="processing">
                            <Chip label="Processing" color="secondary" size="small" sx={{ minWidth: 70 }} />
                          </MenuItem>
                          <MenuItem value="assigned">
                            <Chip
                              label="Assigned"
                              size="small"
                              sx={{
                                minWidth: 70,
                                bgcolor: '#2e7d32',
                                color: '#fff',
                                '& .MuiChip-label': { fontWeight: 500 }
                              }}
                            />
                          </MenuItem>
                          <MenuItem value="out_for_delivery" disabled={order.isSplitParent === true}>
                            <Chip 
                              label="Out for Delivery" 
                              size="small" 
                              sx={{ 
                                minWidth: 70,
                                backgroundColor: '#15b083',
                                color: 'white',
                                '& .MuiChip-label': {
                                  fontWeight: 500
                                }
                              }} 
                            />
                          </MenuItem>
                          <MenuItem 
                            value="delivered"
                            disabled={order.isSplitParent === true || getOrderStatus(order) !== 'out_for_delivery'}
                          >
                            <Chip label="Delivered" color="success" size="small" sx={{ minWidth: 70 }} />
                          </MenuItem>
                          <MenuItem value="partial_delivery">
                            <Chip label="Partial Delivery" color="secondary" size="small" sx={{ minWidth: 70 }} />
                          </MenuItem>
                          <MenuItem value="returned">
                            <Chip label="Returned" color="error" size="small" sx={{ minWidth: 70, backgroundColor: '#ff9800', color: 'white', '& .MuiChip-label': { fontWeight: 500 } }} />
                          </MenuItem>
                          {isSuperAdmin && (
                            <MenuItem value="cancelled">
                              <Chip label="Cancelled" color="error" size="small" sx={{ minWidth: 70 }} />
                            </MenuItem>
                          )}
                        </Select>
                        {updatingStatus[getOrderId(order)] && (
                          <CircularProgress 
                            size={16} 
                            sx={{ 
                              position: 'absolute', 
                              right: 8, 
                              top: '50%', 
                              transform: 'translateY(-50%)'
                            }} 
                          />
                        )}
                      </FormControl>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                        {getPaymentMethod(order)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Button
                        variant="outlined"
                        color="primary"
                        size="small"
                        onClick={() => handleViewOrder(getOrderId(order))}
                        sx={{ minWidth: 'auto' }}
                      >
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                  ))}

                  {inStoreOrders.length > 0 && otherOrders.length > 0 && (
                    <>
                    <TableRow>
                      <TableCell
                        colSpan={11}
                        sx={{
                          p: 0,
                          height: 14,
                          backgroundColor: 'background.default'
                        }}
                      />
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={11} sx={{ py: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="body2" color="text.secondary" fontWeight={600}>
                          Online Orders
                        </Typography>
                      </TableCell>
                    </TableRow>
                    </>
                  )}

                  {otherOrders.map((order) => (
                    <TableRow key={getOrderId(order)} hover>
                      <TableCell padding="checkbox">
                        <input
                          type="checkbox"
                          checked={selectedOrders.includes(getOrderId(order))}
                          onChange={() => handleSelectOrder(getOrderId(order))}
                          style={{ cursor: 'pointer' }}
                        />
                      </TableCell>
                      <TableCell>
                        {renderOrderIdCell(order)}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(getOrderDate(order))}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {renderCustomerCell(order)}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {getStoreName(order)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {getDeliveryDate(order)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {getTimeSlotDisplay(order)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {formatCurrency(getOrderAmount(order))}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <FormControl size="small" sx={{ minWidth: 140 }}>
                          <Select
                            value={getOrderStatus(order)}
                            onChange={(e) => handleStatusChange(getOrderId(order), e.target.value)}
                            disabled={updatingStatus[getOrderId(order)]}
                            sx={{
                              '& .MuiSelect-select': {
                                py: 1,
                                fontSize: '0.875rem'
                              }
                            }}
                          >
                            <MenuItem value="pending">
                              <Chip label="Pending" color="info" size="small" sx={{ minWidth: 70 }} />
                            </MenuItem>
                            <MenuItem value="confirmed">
                              <Chip label="Confirmed" color="warning" size="small" sx={{ minWidth: 70 }} />
                            </MenuItem>
                            <MenuItem value="processing">
                              <Chip label="Processing" color="secondary" size="small" sx={{ minWidth: 70 }} />
                            </MenuItem>
                            <MenuItem value="assigned">
                              <Chip
                                label="Assigned"
                                size="small"
                                sx={{
                                  minWidth: 70,
                                  bgcolor: '#2e7d32',
                                  color: '#fff',
                                  '& .MuiChip-label': { fontWeight: 500 }
                                }}
                              />
                            </MenuItem>
                            <MenuItem value="out_for_delivery" disabled={order.isSplitParent === true}>
                              <Chip
                                label="Out for Delivery"
                                size="small"
                                sx={{
                                  minWidth: 70,
                                  backgroundColor: '#15b083',
                                  color: 'white',
                                  '& .MuiChip-label': {
                                    fontWeight: 500
                                  }
                                }}
                              />
                            </MenuItem>
                            <MenuItem
                              value="delivered"
                              disabled={order.isSplitParent === true || getOrderStatus(order) !== 'out_for_delivery'}
                            >
                              <Chip label="Delivered" color="success" size="small" sx={{ minWidth: 70 }} />
                            </MenuItem>
                            <MenuItem value="partial_delivery">
                              <Chip label="Partial Delivery" color="secondary" size="small" sx={{ minWidth: 70 }} />
                            </MenuItem>
                            <MenuItem value="returned">
                              <Chip label="Returned" color="error" size="small" sx={{ minWidth: 70, backgroundColor: '#ff9800', color: 'white', '& .MuiChip-label': { fontWeight: 500 } }} />
                            </MenuItem>
                            {isSuperAdmin && (
                              <MenuItem value="cancelled">
                                <Chip label="Cancelled" color="error" size="small" sx={{ minWidth: 70 }} />
                              </MenuItem>
                            )}
                          </Select>
                          {updatingStatus[getOrderId(order)] && (
                            <CircularProgress
                              size={16}
                              sx={{
                                position: 'absolute',
                                right: 8,
                                top: '50%',
                                transform: 'translateY(-50%)'
                              }}
                            />
                          )}
                        </FormControl>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                          {getPaymentMethod(order)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Button
                          variant="outlined"
                          color="primary"
                          size="small"
                          onClick={() => handleViewOrder(getOrderId(order))}
                          sx={{ minWidth: 'auto' }}
                        >
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalOrders}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* Order Modal */}
      <OrderModal 
        open={orderModalOpen}
        onClose={handleCloseOrderModal}
        order={selectedOrder}
        onOrderUpdated={handleOrderUpdated}
        onPartialDelivery={(orderToSplit) => {
          setPartialDeliveryOrder(orderToSplit);
          setPartialDeliveryOpen(true);
        }}
      />

      <PartialDeliveryModal
        open={partialDeliveryOpen}
        order={partialDeliveryOrder}
        onClose={() => {
          setPartialDeliveryOpen(false);
          setPartialDeliveryOrder(null);
        }}
        onCreated={() => {
          setPartialDeliveryOpen(false);
          setPartialDeliveryOrder(null);
          handleOrderUpdated();
        }}
      />

      {/* Status Reason Dialog */}
      <StatusReasonDialog
        open={statusReasonDialog.open}
        onClose={handleStatusReasonCancel}
        onConfirm={handleStatusReasonConfirm}
        status={statusReasonDialog.newStatus}
        orderID={statusReasonDialog.orderId}
        loading={statusReasonDialog.loading}
      />

      {/* Rider Allocation Modal */}
      <RiderAllocationModal
        open={allocationModalOpen}
        onClose={handleCloseAllocationModal}
        selectedOrders={selectedOrders}
        onAllocate={handleOrderAllocation}
      />

      {/* Self-contained map route planner — delete this + the toolbar button to remove the feature */}
      <OrderMapSelector
        open={routePlannerOpen}
        onClose={() => setRoutePlannerOpen(false)}
        onOrdersChanged={handleRefresh}
      />
      
      {/* Duplicate Order Error Modal */}
      <Dialog
        open={duplicateOrderError.open}
        onClose={handleCloseDuplicateError}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: 'error.main' }}>
          Orders Already Assigned to Routes
        </DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            The following orders are already assigned to routes and cannot be added to a new route:
          </Alert>
          <Box sx={{ mt: 2 }}>
            {duplicateOrderError.duplicateOrders.map((dup, index) => (
              <Box
                key={index}
                sx={{
                  p: 2,
                  mb: 1,
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  backgroundColor: 'background.paper'
                }}
              >
                <Typography variant="body1" fontWeight="bold">
                  Order #{dup.orderId}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Already assigned to Route: {dup.routeId}
                </Typography>
              </Box>
            ))}
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Please deselect these orders and try again.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDuplicateError} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OrderList; 